import {
  CalendarIcon,
  ChevronDoubleLeftIcon,
  CurrencyDollarIcon,
  RefreshIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SpeakerphoneIcon,
  TrendingUpIcon,
} from '@heroicons/react/outline';
import { CardBackground } from 'components/ui';
import { useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useUpdateOnboardingSettingsMutation } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { objectivesList } from './descriptions';
import { StepsLayout } from './StepsLayout';
import { useOnboardingState } from './useOnboardingState';

export default function ObjectivesStep() {
  const history = useHistory();
  const { objectives, setObjectives, localStorageKey } = useOnboardingState();

  const { rawQueryParams } = useParseURLQueryParams();
  const queryParams = `store_name=${rawQueryParams?.store_name}`;

  const [updateOnboardingSettings] = useUpdateOnboardingSettingsMutation();

  const handleOnNext = () => {
    const savedProgress = localStorage.getItem(localStorageKey);

    localStorage.setItem(
      localStorageKey,
      JSON.stringify({
        ...(savedProgress ? JSON.parse(savedProgress) : {}),
        objectives,
      })
    );

    updateOnboardingSettings({
      serializedStoreName: queryParams,
      body: {
        objectives: objectives,
      },
    });
    history.push({
      ...history.location,
      pathname: '/onboarding/recommended_segments',
    });
  };

  useEffect(() => {
    const currentOnboarding = localStorage.getItem(localStorageKey);

    if (currentOnboarding) {
      const { objectives } = JSON.parse(currentOnboarding);

      if (objectives?.length) {
        setObjectives(objectives);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepsLayout>
      <div className="px-2.5 mt-4 lg:grid lg:grid-cols-8 lg:mt-10 lg:gap-x-9 lg:px-0">
        <div className="col-span-3 px-2.5 lg:px-0">
          <p className="text-black text-opacity-30">2/4</p>
          <p className="mt-4 text-2xl font-bold lg:leading-8 lg:text-segments-32px">
            What are your top three business goals?
          </p>

          <div className="mt-4">
            <p>
              By sharing your goals, we can provide better recommendations
              around what segments you should focus on targeting.
            </p>
          </div>
        </div>
        <div className="col-span-5 mt-10 lg:mt-0 ">
          <CardBackground>
            <div className="px-5 lg:p-10 py-7">
              <p className="font-bold">
                What are your top three business goals? I want to…
              </p>
              <div className="grid grid-cols-2 gap-3 my-10 xl:grid-cols-4">
                {objectivesList.map((objective) => (
                  <CardBox
                    value={objective.value}
                    label={objective.label}
                    key={objective.value}
                  />
                ))}
              </div>
              <SelectedObjectivesCounter />
            </div>
          </CardBackground>
        </div>
      </div>

      <div className="flex items-center justify-center my-10 space-x-8 ">
        <Link
          to={{
            ...history.location,
            pathname: '/onboarding/profile',
          }}
          className="py-3 font-bold text-black text-opacity-50 lg:px-14"
        >
          Previous
        </Link>
        <button
          onClick={handleOnNext}
          className={`px-14 lg:px-20 py-3 font-bold ${
            objectives.length > 0
              ? 'text-white rounded-xl bg-segments-accent-color-1 '
              : 'text-black text-opacity-50'
          } `}
        >
          {objectives.length > 0 ? 'Next' : 'Skip'}
        </button>
      </div>
    </StepsLayout>
  );
}

function SelectedObjectivesCounter() {
  const { objectives } = useOnboardingState();
  return (
    <div className="text-black text-opacity-50">
      <p>
        {objectives.length}/3 selected{' '}
        {objectives.length === 3 && (
          <span>
            - To help us better understand your priorities, please pick up to 3
            objectives
          </span>
        )}
      </p>
    </div>
  );
}

function CardBox({ value, label }: { value: number; label: string }) {
  const icons: Record<string, JSX.Element> = {
    0: <CalendarIcon className="w-7 h-7" />,
    1: <ChevronDoubleLeftIcon className="w-7 h-7" />,
    2: <ShoppingBagIcon className="w-7 h-7" />,
    3: <ShoppingCartIcon className="w-7 h-7" />,
    4: <SpeakerphoneIcon className="w-7 h-7" />,
    5: <CurrencyDollarIcon className="w-7 h-7" />,
    6: <TrendingUpIcon className="w-7 h-7" />,
    7: <RefreshIcon className="w-7 h-7" />,
  };

  const { objectives, setObjectives } = useOnboardingState();

  const maxObjectives = 3;

  const isCurrentSelected = objectives.includes(value);

  const isDisabled = !!!isCurrentSelected && objectives.length >= maxObjectives;

  return (
    <button
      className={` w-[150px] h-[130px] bg-segments-gray-F8 p-2 shadow-md rounded-3xl disabled:cursor-not-allowed disabled:opacity-50 ${
        isCurrentSelected &&
        'border-segments-link text-segments-link bg-segments-accent-green-3 border-2'
      }`}
      onClick={() => {
        setObjectives(
          isCurrentSelected
            ? objectives.filter((item) => item !== value)
            : [...objectives, value]
        );
      }}
      disabled={isDisabled}
    >
      <div className="flex flex-col items-center justify-center h-full space-y-2 text-center">
        {icons[value]}
        <p className={`text-sm ${isCurrentSelected && 'font-medium'}`}>
          {label}
        </p>
      </div>
    </button>
  );
}
